import type {
  DraftSnapshotSummary,
  SnapshotSummary,
  WorkflowRevisionSummary,
} from './hooks/useWorkflowSnapshots';

export type LegacyPublishedVersionEntry = {
  kind: 'legacy-published';
  id: string;
  version: number;
  versionLabel: string;
  name: string | null;
  timestamp: string;
};

export function formatPublishedVersionLabel(version: number): string {
  return `v${version}`;
}

export function buildLegacyPublishedVersionEntries({
  snapshots,
}: {
  snapshots: SnapshotSummary[];
}): LegacyPublishedVersionEntry[] {
  const publishedEntries: LegacyPublishedVersionEntry[] = snapshots.map((snapshot) => ({
    kind: 'legacy-published',
    id: `legacy:${snapshot.version}`,
    version: snapshot.version,
    versionLabel: formatPublishedVersionLabel(snapshot.version),
    name: snapshot.name,
    timestamp: snapshot.publishedAt,
  }));

  return publishedEntries.sort(compareLegacyPublishedVersionEntries);
}

function compareLegacyPublishedVersionEntries(
  left: LegacyPublishedVersionEntry,
  right: LegacyPublishedVersionEntry
): number {
  const timeCompare = Date.parse(right.timestamp) - Date.parse(left.timestamp);
  if (timeCompare !== 0) return timeCompare;
  return right.version - left.version;
}

/** Label for a date-versioned checkpoint row, e.g. `v2.20260707.191845.167.1ab3213a`. */
export function formatDraftVersionLabel(
  basePublishedVersion: number,
  draftVersion: string
): string {
  return `v${basePublishedVersion}.${draftVersion}`;
}

/**
 * A row in the merged "Revision history" list: numbered revisions (authored
 * mutations, tracked since immutable revision tracking shipped) interleaved
 * reverse-chronologically with date-versioned checkpoints
 * (`workflow_draft_snapshots` rows where `revision_number IS NULL`, still
 * written today by the draft-state sync path — `cofactor workflow sync` and
 * the MCP workflow tool both default `createSnapshot` on).
 */
export type RevisionHistoryEntry =
  | {
      kind: 'revision';
      id: string;
      timestamp: string;
      revision: WorkflowRevisionSummary;
    }
  | {
      kind: 'checkpoint';
      id: string;
      timestamp: string;
      versionLabel: string;
      checkpoint: DraftSnapshotSummary;
    };

/**
 * The `/workflows/:id/snapshots` endpoint returns every date-versioned
 * checkpoint unbounded — production flows range up to ~30, and the backing
 * table holds thousands of rows fleet-wide. Cap the ones shown in the menu so
 * it stays a bounded, scannable list; this is a count cap, not an age filter,
 * so a flow that hasn't synced in months still shows its full numbered
 * revision history plus its most recent checkpoints.
 */
export const MAX_CHECKPOINT_ENTRIES = 20;

export type RevisionHistoryEntries = {
  entries: RevisionHistoryEntry[];
  /** Number of checkpoints omitted by the {@link MAX_CHECKPOINT_ENTRIES} cap; 0 when nothing was truncated. */
  truncatedCheckpointCount: number;
};

export function buildRevisionHistoryEntries({
  revisions,
  draftSnapshots,
}: {
  revisions: WorkflowRevisionSummary[];
  draftSnapshots: DraftSnapshotSummary[];
}): RevisionHistoryEntries {
  const revisionEntries: RevisionHistoryEntry[] = revisions.map((revision) => ({
    kind: 'revision',
    id: `revision:${revision.number}`,
    timestamp: revision.createdAt,
    revision,
  }));

  const sortedCheckpoints = [...draftSnapshots].sort(
    (left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt)
  );
  const recentCheckpoints = sortedCheckpoints.slice(0, MAX_CHECKPOINT_ENTRIES);
  const truncatedCheckpointCount = sortedCheckpoints.length - recentCheckpoints.length;

  const checkpointEntries: RevisionHistoryEntry[] = recentCheckpoints.map((checkpoint) => ({
    kind: 'checkpoint',
    id: `draft:${checkpoint.version}`,
    timestamp: checkpoint.createdAt,
    versionLabel: formatDraftVersionLabel(checkpoint.basePublishedVersion, checkpoint.version),
    checkpoint,
  }));

  return {
    entries: [...revisionEntries, ...checkpointEntries].sort(compareRevisionHistoryEntries),
    truncatedCheckpointCount,
  };
}

function compareRevisionHistoryEntries(
  left: RevisionHistoryEntry,
  right: RevisionHistoryEntry
): number {
  const timeCompare = Date.parse(right.timestamp) - Date.parse(left.timestamp);
  if (timeCompare !== 0) return timeCompare;
  // Deterministic tie-break for identical timestamps: numbered revisions win,
  // otherwise fall back to comparing the raw checkpoint version string.
  if (left.kind === 'revision' && right.kind === 'revision') {
    return right.revision.number - left.revision.number;
  }
  if (left.kind === 'checkpoint' && right.kind === 'checkpoint') {
    return right.checkpoint.version.localeCompare(left.checkpoint.version);
  }
  return left.kind === 'revision' ? -1 : 1;
}
